import React, { useState, useEffect } from 'react';
import { EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import { useToastFunctions } from "../assets/js/toaster";
import { usePortfolioData } from '../context/PortfolioDataContext';
import SectionWithNeuralBg from './SectionWithNeuralBg';
import { CACHE_KEYS, CACHE_TTL, fetchCachedJson, getCached } from '../utils/apiCache';

// Minimal browser info collection for security purposes only
function gatherBrowserInfo() {
  const info = {
    userAgent: navigator.userAgent, // Basic browser/OS info
    timestamp: new Date().toISOString() // Submission time
  };

  return JSON.stringify(info);
}

export default function Contact() {
  const { showErrorMessage, showSuccessMessage, showWarningMessage } = useToastFunctions();
  const { data } = usePortfolioData();
  const { personalInfo } = data;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(null);
  const [contactEmail, setContactEmail] = useState(() => {
    const cached = getCached(CACHE_KEYS.contact);
    return cached?.email || null;
  });

  useEffect(() => {
    if (contactEmail) {
      return undefined;
    }

    let cancelled = false;

    async function loadContactEmail() {
      try {
        const { email } = await fetchCachedJson(
          '/.netlify/functions/contact-info',
          CACHE_KEYS.contact,
          CACHE_TTL.contact,
          { shouldCache: (data) => Boolean(data?.email) }
        );

        if (!cancelled && email) {
          setContactEmail(email);
        }
      } catch {
        // Contact form still works without displaying the email address.
      }
    }

    loadContactEmail();

    return () => {
      cancelled = true;
    };
  }, [contactEmail]);

  const location = [
    personalInfo.location.city,
    personalInfo.location.state,
    personalInfo.location.country,
  ].join(', ');

  async function isTemporaryEmail(email) {
    // Skip validation if RapidAPI keys are not configured
    if (!process.env.REACT_APP_RAPID_API_KEY || !process.env.REACT_APP_RAPID_API_HOST) {
      return false; // Don't block submission if API is not configured
    }

    const url = `https://mailcheck.p.rapidapi.com/?domain=${email}`;
    const options = {
      method: 'GET',
      headers: {
        'X-RapidAPI-Key': process.env.REACT_APP_RAPID_API_KEY,
        'X-RapidAPI-Host': process.env.REACT_APP_RAPID_API_HOST
      }
    };

    try {
      const response = await fetch(url, options);
      const result = await response.json();
      return result.disposable || false;
    } catch (error) {
      // Only log detailed errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Email validation error:', error);
      }
      return false; // Don't block submission on API errors
    }
  }

  function handleFormChange(event) {
    // Sanitize input to prevent XSS attacks
    const sanitizedValue = DOMPurify.sanitize(event.target.value, {
      ALLOWED_TAGS: [], // No HTML tags allowed
      KEEP_CONTENT: true // Keep text content
    });
    setFormData({ ...formData, [event.target.name]: sanitizedValue });
  }

  async function validateForm() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      showWarningMessage("Please fill out all fields.");
      return false;
    }

    if (!emailRegex.test(formData.email)) {
      showWarningMessage("Please enter a valid email address.");
      return false;
    }

    if (submissionCount >= 2) {
      showWarningMessage("You have reached the submission limit (2 per session).");
      return false;
    }

    if (lastSubmissionTime) {
      const currentTime = new Date().getTime();
      const timeDifference = (currentTime - lastSubmissionTime) / 1000;
      if (timeDifference < 60) {
        showWarningMessage("Please wait at least 1 minute before submitting again.");
        return false;
      }
    }

    const isTempEmail = await isTemporaryEmail(formData.email);
    if (isTempEmail) {
      showWarningMessage("Temporary or disposable email addresses are not allowed.");
      return false;
    }

    return true;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);

    const isValid = await validateForm();

    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    sendEmail({
      message: formData.message,
      name: formData.name,
      email: formData.email,
      additional_info: gatherBrowserInfo()
    });
  }

  function sendEmail(variables) {
    window.emailjs.send(
      process.env.REACT_APP_EMAIL_SERVICE_ID,
      process.env.REACT_APP_EMAIL_TEMPLATE_ID,
      variables
    ).then(res => {
      setSubmissionCount(submissionCount + 1);
      setLastSubmissionTime(new Date().getTime());
      showSuccessMessage("Message sent successfully!");
      setFormData({
        name: "",
        email: "",
        message: "",
      });
      setIsSubmitting(false);
    }).catch(err => {
      showErrorMessage("Failed to send message. Please try again.");
      setIsSubmitting(false);
    });
  }

  return (
    <SectionWithNeuralBg id="contact" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Get in Touch
          </h2>
          <p className="text-lg text-gray-400">
            Have a project in mind? Let's discuss how I can help bring your ideas to life.
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-semibold text-white mb-6">
              Contact Information
            </h3>
            <p className="text-gray-400 mb-8">
              Feel free to reach out through any of these channels. I typically respond within 24 hours.
            </p>

            <div className="space-y-6">
              {contactEmail && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-900/30 rounded-lg flex items-center justify-center border border-primary-800/50">
                      <EnvelopeIcon className="w-6 h-6 text-primary-400" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-semibold text-white mb-1">Email</h4>
                    <a href={`mailto:${contactEmail}`} className="text-primary-400 hover:text-primary-300 transition-colors">
                      {contactEmail}
                    </a>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-900/30 rounded-lg flex items-center justify-center border border-blue-800/50">
                    <MapPinIcon className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-sm font-semibold text-white mb-1">Location</h4>
                  <p className="text-gray-400">{location}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className="form-input"
                  placeholder="Your name"
                  autoComplete="name"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  className="form-input"
                  placeholder="your@email.com"
                  autoComplete="email"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  rows="5"
                  className="form-input resize-none"
                  placeholder="Tell me about your project..."
                  autoComplete="off"
                  required
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed justify-center">
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>

              {/* Privacy Notice */}
              <p className="text-xs text-gray-500 mt-4 text-center">
                By submitting this form, you consent to the collection of your contact information and browser details for communication purposes only.
              </p>
            </form>
          </div>
        </div>
      </div>
    </SectionWithNeuralBg>
  );
}
